import MuiIconButton, { IconButtonProps as MuiIconButtonProps } from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import { mergeSx } from "designSystem/styles/style-functions";
import React, { forwardRef, FunctionComponent } from "react";
import { boxShadows } from "designSystem/styles/color";
import { useLocale } from "designSystem/locale";
import {
	darkModeButtonStyle,
	defaultButtonStyle, defaultOnColorButtonStyle, defaultOnGreyButtonStyle, dragHandleButtonStyle,
	ICON_MEDIUM_SIZE,
	ICON_ONLY_SIZE,
	ICON_SMALL_SIZE,
	inactiveButtonStyle,
	outlineButtonStyle,
	primaryIconStyle,
	secondaryStyle
} from "designSystem/styles/style-functions";
import Tooltip, { TooltipProps } from "../tooltip/Tooltip";

export interface IconButtonProps extends MuiIconButtonProps {
	/** custom pixel size of icon to override small or medium size */
	customSize?: number;

	/** design of icon (default is use IconButton styling) */
	design?:
	| "darkMode"
	| "default"
	| "defaultOnGrey"
	| "dragHandle"
	| "iconOnly"
	| "iconOnlyOnGrey"
	| "iconOnlyOnColor"
	| "inactive"
	| "outline"
	| "primary"
	| "secondary";

	/** localization resource to use to lookup button title; can be simple resource or type resource (i.e. "User.Name") */
	res?: string;

	/** parameters to pass if looking up resource */
	resParams?: Array<number | string>;

	/** indicate if button should be round (default false - border radius of 3) */
	round?: boolean;

	/** indicate if box shadow should be added to the button */
	shadow?: boolean;

	/** indicate whether to show tooltip when button disabled */
	showTooltipWhenDisabled?: boolean;

	/** properties for tooltip */
	tooltipProps?: Partial<TooltipProps>;
};

const designs = {
	darkMode: darkModeButtonStyle,
	defaultOnGrey: defaultOnGreyButtonStyle,
	dragHandle: dragHandleButtonStyle,
	iconOnlyOnGrey: defaultOnGreyButtonStyle,
	iconOnlyOnColor: defaultOnColorButtonStyle,
	inactive: inactiveButtonStyle,
	outline: outlineButtonStyle,
	primary: primaryIconStyle,
	secondary: secondaryStyle,
};

const iconOnlyDesign = {
	dragHandle: 1,
	iconOnly: 1,
	iconOnlyOnGrey: 1,
	iconOnlyOnColor: 1,
};

function getBoxSize(customSize: number, iconOnly: boolean, isSmall: boolean): number {
	if (customSize) {
		return customSize;
	} else if (iconOnly) {
		return ICON_ONLY_SIZE;
	}

	return isSmall ? ICON_SMALL_SIZE : ICON_MEDIUM_SIZE;
}

const rootStyles = (props: IconButtonProps, theme) => {
	const { customSize, design, round, shadow, size } = props;
	const styleFunction = designs[design] || defaultButtonStyle;
	const iconOnly = iconOnlyDesign[design];
	const isSmall = (size === "small") || iconOnly;
	const boxSize = getBoxSize(customSize, iconOnly, isSmall);
	const styles = {
		...styleFunction(theme),
		borderRadius: round ? "50%" : "3px",
		borderStyle: "solid",
		borderWidth: iconOnly ? 0 : "1px",
		height: boxSize,
		width: boxSize,
	};

	if (!customSize) {
		const applyStyles = (key: string) => {
			styles[key] = {
				...styles[key],
				fontSize: isSmall ? ICON_ONLY_SIZE : 20,
			};
		};

		applyStyles("& .MuiIcon-root");
		applyStyles("& .MuiSvgIcon-root");
		applyStyles("& .svg-inline--fa");
	}

	if (shadow) {
		styles.boxShadow = boxShadows.iconButton;
	}

	return styles;
};

const IconButton: FunctionComponent<IconButtonProps> = forwardRef((props, ref) => {
	const loc = useLocale();
	const theme = useTheme();
	const {
		className,
		customSize,
		design,
		disabled,
		onClick,
		res,
		resParams,
		round,
		shadow,
		showTooltipWhenDisabled,
		sx,
		tabIndex,
		title,
		tooltipProps,
		...otherProps
	} = props;

	const tooltip = title || loc.resource(res, resParams);
	const inactive = design === "inactive";
	const button = (
		<MuiIconButton
			aria-label={tooltip}
			className={className || ""}
			data-testid="IconButton"
			disabled={disabled}
			onClick={inactive ? null : onClick}
			ref={ref}
			sx={mergeSx(sx, rootStyles(props, theme))}
			tabIndex={tabIndex ?? (inactive ? -1 : 0)}
			{...otherProps}
		/>
	);
	const tooltipTitle = title || tooltipProps?.title;

	if (tooltip && showTooltipWhenDisabled) {
		return (
			<Tooltip
				{...tooltipProps}
				res={res}
				resParams={resParams}
				title={tooltipTitle}
			>
				<span>
					{button}
				</span>
			</Tooltip>
		);
	} else if (tooltip && !disabled) {
		return (
			<Tooltip
				{...tooltipProps}
				res={res}
				resParams={resParams}
				title={tooltipTitle}
			>
				{button}
			</Tooltip>
		);
	} else {
		return button;
	}
});

export default IconButton;